'use client';
import { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import Button from '../ui/Button';
import { usePathname, useRouter } from 'next/navigation';

const Header = () => {
  const [isSticky, setIsSticky] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const router = useRouter();
  const pathname = usePathname();

  // Sticky scroll detection
  useEffect(() => {
    const handleScroll = () => setIsSticky(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Smooth scroll or redirect to homepage with hash
  const handleSmoothScroll = (
    e: React.MouseEvent<HTMLAnchorElement>,
    id: string
  ) => {
    e.preventDefault();
    setMenuOpen(false);

    const el = document.getElementById(id);

    if (pathname === '/') {
      // Already on homepage — scroll directly
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      // Navigate to homepage with hash
      router.push(`/#${id}`);
    }
  };

  const navItems = [
    { label: 'Home', id: 'home' },
    { label: 'Events', id: 'events' },
    { label: 'Speakers', id: 'speakers' },
    { label: 'Event Highlights', id: 'event-highlights' },
    { label: 'About Us', id: 'about' },
    { label: 'Partners', id: 'partners' },
  ];

  return (
    <header data-component-id="src/components/common/Header.tsx:53:4" data-component-path="src/components/common/Header.tsx" data-component-line="53" data-component-file="Header.tsx" data-component-name="header" data-component-content="%7B%22elementName%22%3A%22header%22%2C%22className%22%3A%22w-full%20fixed%20top-0%20left-0%20z-50%20bg-black%20transition-all%20duration-300%22%7D" className="w-full fixed top-0 left-0 z-50 bg-black transition-all duration-300">
      <div data-component-id="src/components/common/Header.tsx:54:6" data-component-path="src/components/common/Header.tsx" data-component-line="54" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
        className={`w-full ${
          isSticky
            ? 'max-w-[1440px] px-4 mx-auto'
            : 'max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8'
        }`}
      >
        <div data-component-id="src/components/common/Header.tsx:61:8" data-component-path="src/components/common/Header.tsx" data-component-line="61" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
          className={`flex justify-between items-center transition-all duration-300 ${
            isSticky
              ? 'rounded-none mt-0 border-none shadow-none'
              : 'rounded-4xl mt-8 border border-border-white-high'
          } p-3`}
        >
          {/* Logo */}
          <div data-component-id="src/components/common/Header.tsx:69:10" data-component-path="src/components/common/Header.tsx" data-component-line="69" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20ml-3%22%7D" className="flex items-center ml-3">
            <Link data-component-id="src/components/common/Header.tsx:70:12" data-component-path="src/components/common/Header.tsx" data-component-line="70" data-component-file="Header.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2F%22%7D" href='/'>
            <Image data-component-id="src/components/common/Header.tsx:71:12" data-component-path="src/components/common/Header.tsx" data-component-line="71" data-component-file="Header.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22src%22%3A%22%2Fimages%2Faicapital-connect-logo.svg%22%2C%22alt%22%3A%22aicapital-connect-logo%22%2C%22className%22%3A%22w-%5B68px%5D%20h-%5B16px%5D%20sm%3Aw-%5B102px%5D%20sm%3Ah-%5B24px%5D%20lg%3Aw-%5B136px%5D%20lg%3Ah-%5B32px%5D%22%7D"
              src="/images/aicapital-connect-logo.svg"
              alt="aicapital-connect-logo"
              width={136}
              height={32}
              className="w-[68px] h-[16px] sm:w-[102px] sm:h-[24px] lg:w-[136px] lg:h-[32px]"
            />
            </Link>
          </div>

          {/* Hamburger Menu Icon with animation */}
          <button data-component-id="src/components/common/Header.tsx:82:10" data-component-path="src/components/common/Header.tsx" data-component-line="82" data-component-file="Header.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22lg%3Ahidden%20p-2%20relative%20z-50%22%7D"
            className="lg:hidden p-2 relative z-50"
            aria-label="Toggle Menu"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <div data-component-id="src/components/common/Header.tsx:87:12" data-component-path="src/components/common/Header.tsx" data-component-line="87" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-6%20h-6%20flex%20flex-col%20justify-center%20items-center%20relative%22%7D" className="w-6 h-6 flex flex-col justify-center items-center relative">
              <span data-component-id="src/components/common/Header.tsx:88:14" data-component-path="src/components/common/Header.tsx" data-component-line="88" data-component-file="Header.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D"
                className={`block w-6 h-0.5 bg-white transition-all duration-300 ${
                  menuOpen ? 'rotate-45 translate-y-1.5' : ''
                }`}
              />
              <span data-component-id="src/components/common/Header.tsx:93:14" data-component-path="src/components/common/Header.tsx" data-component-line="93" data-component-file="Header.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D"
                className={`block w-6 h-0.5 bg-white my-1 transition-all duration-300 ${
                  menuOpen ? 'opacity-0' : ''
                }`}
              />
              <span data-component-id="src/components/common/Header.tsx:98:14" data-component-path="src/components/common/Header.tsx" data-component-line="98" data-component-file="Header.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D"
                className={`block w-6 h-0.5 bg-white transition-all duration-300 ${
                  menuOpen ? '-rotate-45 -translate-y-1.5' : ''
                }`}
              />
            </div>
          </button>

          {/* Nav Links */}
          <nav data-component-id="src/components/common/Header.tsx:107:10" data-component-path="src/components/common/Header.tsx" data-component-line="107" data-component-file="Header.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%7D"
            className={`${
              menuOpen ? 'block' : 'hidden'
            } lg:block absolute lg:relative top-full lg:top-auto left-0 lg:left-auto w-full lg:w-auto bg-black lg:bg-transparent border-t lg:border-none mt-0 lg:mt-0 p-4 lg:p-0 z-40`}
          >
            <div data-component-id="src/components/common/Header.tsx:112:12" data-component-path="src/components/common/Header.tsx" data-component-line="112" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20lg%3Aflex-row%20gap-4%20lg%3Agap-12%20items-start%20lg%3Aitems-center%22%7D" className="flex flex-col lg:flex-row gap-4 lg:gap-12 items-start lg:items-center">
              <div data-component-id="src/components/common/Header.tsx:113:14" data-component-path="src/components/common/Header.tsx" data-component-line="113" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20lg%3Aflex-row%20gap-4%20lg%3Agap-12%20w-full%20lg%3Aw-auto%22%7D" className="flex flex-col lg:flex-row gap-4 lg:gap-12 w-full lg:w-auto">
                {navItems.map((item) => (
                  <a data-component-id="src/components/common/Header.tsx:115:18" data-component-path="src/components/common/Header.tsx" data-component-line="115" data-component-file="Header.tsx" data-component-name="a" data-component-content="%7B%22elementName%22%3A%22a%22%2C%22className%22%3A%22text-md%20font-normal%20leading-lg%20text-left%20text-white%20hover%3Atext-gray-300%20transition-colors%20duration-200%20py-2%20lg%3Apy-0%22%7D"
                    key={item.id}
                    href={`#${item.id}`}
                    onClick={(e) => handleSmoothScroll(e, item.id)}
                    className="text-md font-normal leading-lg text-left text-white hover:text-gray-300 transition-colors duration-200 py-2 lg:py-0"
                  >
                    {item.label}
                  </a>
                ))}
              </div>

              {/* Contact Us Button */}
              <div data-component-id="src/components/common/Header.tsx:127:14" data-component-path="src/components/common/Header.tsx" data-component-line="127" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20lg%3Aw-auto%20mt-4%20lg%3Amt-0%22%7D" className="w-full lg:w-auto mt-4 lg:mt-0">
                <Link data-component-id="src/components/common/Header.tsx:128:16" data-component-path="src/components/common/Header.tsx" data-component-line="128" data-component-file="Header.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2Fcontact-us%2F%22%7D" href="/contact-us/" onClick={() => setMenuOpen(false)}>
                  <Button data-component-id="src/components/common/Header.tsx:129:18" data-component-path="src/components/common/Header.tsx" data-component-line="129" data-component-file="Header.tsx" data-component-name="Button" data-component-content="%7B%22elementName%22%3A%22Button%22%2C%22className%22%3A%22w-full%20lg%3Aw-auto%20px-6%20py-3.5%20lg%3Apx-12%20lg%3Apy-3.5%20gap-2%20text-md%20font-bold%20leading-lg%20text-center%20text-secondary-foreground%20border%20border-white%20rounded-3xl%20bg-white%20hover%3Abg-white%20hover%3Atext-black%20transition-all%20duration-200%20flex%20items-center%20justify-center%22%7D"
                    text="Contact Us"
                    variant="outline"
                    className="w-full lg:w-auto px-6 py-3.5 lg:px-12 lg:py-3.5 gap-2 text-md font-bold leading-lg text-center text-secondary-foreground border border-white rounded-3xl bg-white hover:bg-white hover:text-black transition-all duration-200 flex items-center justify-center"
                  >
                    <span data-component-id="src/components/common/Header.tsx:134:20" data-component-path="src/components/common/Header.tsx" data-component-line="134" data-component-file="Header.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-black%22%2C%22textContent%22%3A%22Contact%20Us%22%7D" className='text-black'>Contact Us</span>
                    {/* <Image
                      src="/images/img-arrowright.svg"
                      alt="img-arrowright"
                      width={24}
                      height={24}
                      className="w-6 h-6"
                    /> */}
                  </Button>
                </Link>
              </div>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
